/**
 * Persistance locale (IndexedDB).
 *
 * # Pourquoi pas localStorage
 *
 * localStorage est synchrone — il bloque le thread d'affichage — et plafonné à
 * ~5 Mo. Surtout, il ne convient pas à une file d'attente : une écriture
 * interrompue peut y laisser un état partiel. IndexedDB est transactionnel,
 * ce qui est exactement ce qu'exige la règle « la file n'est jamais vidée sans
 * confirmation du serveur ».
 *
 * localStorage ne conserve que deux valeurs, minuscules et non critiques à
 * l'écriture : le token de foyer et l'identifiant d'appareil.
 *
 * # Deux zones distinctes
 *
 *   - `lists` / `items` / `devices` : **l'état serveur connu**, tel quel ;
 *   - `queue` : les opérations locales **non encore acquittées**.
 *
 * Elles ne sont jamais mélangées. Ce que l'utilisateur voit est la projection
 * de la seconde sur la première (cf. shared/merge.ts), recalculée à la volée.
 * Conséquence : une opération acquittée disparaît de la file sans qu'aucun
 * état affiché n'ait à être « corrigé ».
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Op } from '@shared/ops.ts';
import type { Device, Item, List, SyncDelta } from '@shared/types.ts';
import { emptyState, type HouseholdState } from '@shared/merge.ts';

const DB_NAME = 'todofamille';
const DB_VERSION = 1;

/** Opération en file, avec son rang d'insertion : la file est strictement FIFO. */
export type QueuedOp = Op & { seq: number };

interface TodoDB extends DBSchema {
  lists: { key: string; value: List };
  items: { key: string; value: Item };
  devices: { key: string; value: Device };
  queue: { key: string; value: QueuedOp; indexes: { seq: number } };
  meta: { key: string; value: number | string };
}

let handle: Promise<IDBPDatabase<TodoDB>> | null = null;

function db(): Promise<IDBPDatabase<TodoDB>> {
  handle ??= openDB<TodoDB>(DB_NAME, DB_VERSION, {
    upgrade(database) {
      database.createObjectStore('lists', { keyPath: 'id' });
      database.createObjectStore('items', { keyPath: 'id' });
      database.createObjectStore('devices', { keyPath: 'id' });
      database.createObjectStore('queue', { keyPath: 'op_id' }).createIndex('seq', 'seq');
      database.createObjectStore('meta');
    },
  });

  return handle;
}

/**
 * Demande au navigateur de ne pas purger ces données.
 *
 * Décisif sur iOS : Safari efface les données des sites non visités depuis
 * 7 jours. Les PWA installées sur l'écran d'accueil en sont exemptées — d'où
 * l'insistance de l'application à proposer l'installation.
 */
export async function requestPersistence(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;

  try {
    return (await navigator.storage.persisted()) || (await navigator.storage.persist());
  } catch {
    return false;
  }
}

// --- état serveur ----------------------------------------------------------

export async function loadServerState(): Promise<HouseholdState> {
  const database = await db();
  const state = emptyState();

  const [lists, items, devices, cursor] = await Promise.all([
    database.getAll('lists'),
    database.getAll('items'),
    database.getAll('devices'),
    database.get('meta', 'cursor'),
  ]);

  for (const list of lists) state.lists.set(list.id, list);
  for (const item of items) state.items.set(item.id, item);
  for (const device of devices) state.devices.set(device.id, device);
  state.cursor = typeof cursor === 'number' ? cursor : 0;

  return state;
}

/**
 * Écrit un delta.
 *
 * Le curseur n'est avancé que dans la **même transaction** que les lignes : une
 * interruption ne peut pas laisser un curseur en avance sur les données, ce qui
 * ferait manquer des lignes définitivement.
 */
export async function persistDelta(delta: SyncDelta): Promise<void> {
  const database = await db();
  const tx = database.transaction(['lists', 'items', 'devices', 'meta'], 'readwrite');

  await Promise.all([
    ...delta.lists.map((row) => tx.objectStore('lists').put(row)),
    ...delta.items.map((row) => tx.objectStore('items').put(row)),
    ...delta.devices.map((row) => tx.objectStore('devices').put(row)),
    tx.objectStore('meta').put(delta.cursor, 'cursor'),
    tx.done,
  ]);
}

// --- file d'attente --------------------------------------------------------

export async function loadQueue(): Promise<Op[]> {
  const database = await db();
  const queued = await database.getAllFromIndex('queue', 'seq');

  return queued.map(({ seq, ...op }) => {
    void seq;
    return op as Op;
  });
}

export async function enqueueOp(op: Op): Promise<void> {
  const database = await db();
  const tx = database.transaction(['queue', 'meta'], 'readwrite');

  const previous = await tx.objectStore('meta').get('seq');
  const seq = (typeof previous === 'number' ? previous : 0) + 1;

  await Promise.all([
    tx.objectStore('queue').put({ ...op, seq }),
    tx.objectStore('meta').put(seq, 'seq'),
    tx.done,
  ]);
}

/**
 * Retire les opérations que le serveur a **explicitement acquittées**.
 *
 * C'est le seul chemin par lequel une opération quitte la file. Ni un timeout,
 * ni un 429, ni une erreur de quota ne suppriment quoi que ce soit : dans le
 * doute, on conserve et on rejoue.
 */
export async function acknowledgeOps(opIds: readonly string[]): Promise<void> {
  if (opIds.length === 0) return;

  const database = await db();
  const tx = database.transaction('queue', 'readwrite');

  await Promise.all([...opIds.map((id) => tx.store.delete(id)), tx.done]);
}

/**
 * Retire les opérations que le serveur a rejetées comme malformées. Les
 * rejouer ne servirait à rien : elles resteraient éternellement en file et
 * bloqueraient l'affichage du nombre d'opérations en attente.
 */
export async function discardOps(opIds: readonly string[]): Promise<void> {
  await acknowledgeOps(opIds);
}

// --- réinitialisation ------------------------------------------------------

/** Efface tout l'état local. Utilisé au changement de foyer. */
export async function clearAll(): Promise<void> {
  const database = await db();
  const tx = database.transaction(['lists', 'items', 'devices', 'queue', 'meta'], 'readwrite');

  await Promise.all([
    tx.objectStore('lists').clear(),
    tx.objectStore('items').clear(),
    tx.objectStore('devices').clear(),
    tx.objectStore('queue').clear(),
    tx.objectStore('meta').clear(),
    tx.done,
  ]);
}
